"use client";
import { useCallback, useState } from "react";
import { Zap } from "../../zap/useSearchZaps";
import {
  useQueryZapPermissions,
  ZapPermissionChecks,
} from "./useQueryZapPermissions";
import { Response, defaultResponse } from "./useZapActionPermissions";
import { HydratedZapAsset } from "@zapier/bff";

interface Input {
  zap: Zap | HydratedZapAsset | null | undefined;
  zapUUID?: string;
  isImportExportEnabled: boolean;
  enabled?: boolean;
}

export function useProjectZapActionPermissions({
  zap,
  zapUUID,
  isImportExportEnabled,
  enabled = true,
}: Input): Response | undefined {
  // a toggleable state to opt in for fetching advanced permissions beyond can_edit
  const [advancedPermissionsEnabled, setAdvancedPermissionsEnabled] =
    useState(false);
  const enableAdvancedPermissions = useCallback(() => {
    setAdvancedPermissionsEnabled(true);
  }, []);

  const projectId = zap?.projectId;

  const zapId = zapUUID || zap?.searchZap?.zapId;

  // fetch can_edit check from the Permission API by default
  const { can: fgaCanEdit } = useQueryZapPermissions({
    id: zapId,
    check: ZapPermissionChecks.CanEdit,
    projectId,
  });

  // only fetch can_delete when advanced permissions are enabled
  const { can: fgaCanDelete } = useQueryZapPermissions(
    {
      id: zapId,
      check: ZapPermissionChecks.CanDelete,
      projectId,
    },
    { enabled: advancedPermissionsEnabled && enabled && !!zapId },
  );
  // only fetch can_delete when advanced permissions are enabled
  const { can: fgaCanChangeOwner } = useQueryZapPermissions(
    {
      id: zapId,
      check: ZapPermissionChecks.CanChangeOwner,
      projectId,
    },
    { enabled: advancedPermissionsEnabled && enabled && !!zapId },
  );

  /** If there is no projectId then don't try to calculate permissions */
  if (!projectId) {
    return;
  }
  if (!enabled) {
    return defaultResponse;
  }

  const canChangeOwner = () => {
    return fgaCanChangeOwner || false;
  };
  const canDelete = () => {
    return fgaCanDelete || false;
  };
  const canEdit = () => {
    return fgaCanEdit;
  };
  const canEnableDisable = () => {
    return fgaCanEdit;
  };
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const canMoveZapToFolder = () => {
    return false;
  };
  const canMoveZapToProject = () => {
    return fgaCanEdit;
  };
  const canRename = () => {
    return fgaCanEdit;
  };
  const canShare = () => {
    return fgaCanEdit;
  };
  const slackWorkflowBuilderUrl =
    (zap?.sourceUrl?.includes("app.slack.com") && zap?.sourceUrl) || undefined;

  return {
    ...defaultResponse,
    canChangeOwner: canChangeOwner(),
    canCreateTemplate: true, // anyone with view access
    canDelete: canDelete(),
    canDuplicate: true, // anyone with view access
    canEdit: canEdit(),
    canEnableDisable: canEnableDisable(),
    canExport: isImportExportEnabled,
    canMoveZapToFolder: canMoveZapToFolder(),
    canMoveZapToProject: canMoveZapToProject(),
    canRename: canRename(),
    canShare: canShare(),
    canShowDrafts: true, // anyone with view access
    canViewZapHistory: true, // anyone with view access
    slackWorkflowBuilderUrl,
    enableAdvancedPermissions,
  };
}
